import { Tag } from "antd";
import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  QueryFieldMapping,
} from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createStaticOptionsRequest,
} from "../../hooks/useSearchRequest";
import { getWellbeingMaterialCategories } from "../../repository/data.repository";
import { WellbeingMaterialType } from "../../repository/data.default";

export const materialTypeOptions: { value: WellbeingMaterialType; display: string }[] =
  [
    { value: "Article", display: "Article" },
    { value: "Video", display: "Video" },
    { value: "Document", display: "Document" },
    { value: "Link", display: "Link" },
  ];

// A static-options combobox request (the Type enum is a fixed set, not a searchable endpoint).
export const typeRequest = createStaticOptionsRequest(materialTypeOptions);

// Per-type tag colors, so the Type column reads consistently with the Public/Published tags.
const materialTypeColors: Record<WellbeingMaterialType, string> = {
  Article: "blue",
  Video: "purple",
  Document: "cyan",
  Link: "gold",
};

// Published / Public are booleans on the material; expose them as fixed-option filters mirroring the columns.
const publishedRequest = createStaticOptionsRequest([
  { display: "Published", value: "true" },
  { display: "Draft", value: "false" },
]);
const publicRequest = createStaticOptionsRequest([
  { display: "Public", value: "true" },
  { display: "Private", value: "false" },
]);

export const wellbeingMaterialsViewColumns: DataColumn[] = [
  {
    title: "Name",
    key: "name",
    width: 224,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Type",
    key: "type",
    width: 130,
    sort: true,
    transform: (value: WellbeingMaterialType) => (
      <Tag color={materialTypeColors[value] ?? "default"}>{value}</Tag>
    ),
  },
  {
    title: "Category",
    key: "categoryName",
    width: 176,
    sort: true,
  },
  {
    title: "Public",
    key: "isPublic",
    width: 110,
    sort: true,
    transform: (value: any) => (
      <Tag color={value ? "green" : "default"}>{value ? "Public" : "Private"}</Tag>
    ),
  },
  {
    title: "Published",
    key: "isPublished",
    width: 120,
    sort: true,
    transform: (value: any) => (
      <Tag color={value ? "green" : "default"}>{value ? "Published" : "Draft"}</Tag>
    ),
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 168,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
];

const wellbeingMaterialsQueryMappings: QueryFieldMapping[] = [
  { formField: "name" },
  {
    formField: "type",
    queryField: "type",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "category",
    queryField: "categoryUid",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "isPublished",
    queryField: "isPublished",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
  {
    formField: "isPublic",
    queryField: "isPublic",
    operation: QueryOperation.Equal,
    transform: comboboxValue,
  },
];

export const mapWellbeingMaterialsViewQuery = (values: any): SearchQuery =>
  buildSearchQuery(values, wellbeingMaterialsQueryMappings);

export const wellbeingMaterialsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "textbox", name: "name", label: "Name" },
        {
          type: "combobox",
          name: "type",
          label: "Type",
          request: typeRequest,
        },
        {
          type: "combobox",
          name: "category",
          label: "Category",
          request: createSearchRequest(getWellbeingMaterialCategories, "name"),
        },
        {
          type: "combobox",
          name: "isPublished",
          label: "Published",
          request: publishedRequest,
        },
        {
          type: "combobox",
          name: "isPublic",
          label: "Public",
          request: publicRequest,
        },
      ],
    },
  ],
};
